'use client';

import { c as _c } from "react/compiler-runtime";
import { hasNoLoginMethod } from '@shared/all/domains/authMethods';
import useUser from '@shared/utils/hooks/useUser';
import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocationSearchParam } from '../../hooks/useLocationSearchParam';
import usePresence from '../../hooks/usePresence';
import { useShouldShowOnboardingOverlay } from '../../utils/onboardingStorage';
import ActivationFunnel from '../onboarding/activationFunnel/ActivationFunnel';
import Onboarding from '../onboarding/Onboarding';

// Root-level overlay, not a route: the funnel animates in over the current page
// (no navigation, so no route-load race or flash underneath). Portaled to <body>
// so it stacks above the scroll container instead of inside it.
//
// The portal must stay empty through hydration. `show` is NOT reliably false on
// the first client render — the user can resolve synchronously from the client
// cookie — and a `typeof document` check makes the server render nothing while
// that first client render draws the overlay, which React reports as a
// hydration mismatch. `mounted` comes from useSyncExternalStore: React uses the
// server snapshot (false) for the hydrating render and re-renders straight away
// with the client snapshot (true), so the overlay still appears on the next
// synchronous pass, with no visible delay.
const subscribeToNothing = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;
const OnboardingGate = () => {
  const $ = _c(5);
  const mounted = useSyncExternalStore(subscribeToNothing, clientSnapshot, serverSnapshot);
  const {
    currentUser
  } = useUser();
  const isEmbed = useLocationSearchParam("embed") === "true";
  const shouldShowOverlay = useShouldShowOnboardingOverlay(currentUser);
  const show = !isEmbed && shouldShowOverlay;
  const {
    rendered,
    exiting
  } = usePresence(show, 350);
  if (!mounted || !rendered) {
    return null;
  }
  const t0 = `fixed inset-0 z-[150] bg-background-primary [--enter-duration:350ms] [--enter-ease:cubic-bezier(0.4,0,0.2,1)] [--exit-duration:350ms] [--exit-ease:cubic-bezier(0.4,0,0.2,1)] ${exiting ? "anim-exit" : "anim-enter"}`;
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasNoLoginMethod(currentUser) ? <Onboarding /> : <ActivationFunnel />;
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = createPortal(<div data-onboarding-overlay={true} className={t0}>{t1}</div>, document.body);
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export default OnboardingGate;